import GenericMultipleSelect from "@convin/components/select/GenericMultipleSelect";
import GenericSingleSelect from "@convin/components/select/GenericSingleSelect";
import useGlobalFiltersStateContext from "@convin/hooks/useGlobalFiltersStateContext";
import {
    useGetEntityListQuery,
    useLazySearchEntityQuery,
} from "@convin/redux/services/meeting/filters.service";
import { isDefined } from "@convin/utils/helper/common.helper";
import { Box, createFilterOptions } from "@mui/material";
import { debounce } from "lodash";
import { useCallback } from "react";

export type EntityValueType = {
    id: string | number;
    label: string;
};
const filterOptions = createFilterOptions<EntityValueType>({
    ignoreCase: true,
});

export const EntityFilter = () => {
    const {
        state: {
            entity: {
                name,
                value,
                isEntityOpen,
                selectedEntities,
                searchEntityResults,
            },
        },
        updateState,
    } = useGlobalFiltersStateContext();
    const { data: entityList, isLoading: entityListLoading } =
        useGetEntityListQuery(undefined, { refetchOnMountOrArgChange: true });
    const [searchEntity, { isFetching: searchResultsFetching }] =
        useLazySearchEntityQuery();

    const debouncedSearchEntity = useCallback(
        debounce((query, entity_id) => {
            searchEntity({
                query,
                entity_id,
            })
                .unwrap()
                .then((res) => {
                    const combinedResults = [
                        ...(res ?? []),
                        ...(selectedEntities ?? []),
                    ];
                    updateState({
                        entity: {
                            name,
                            value,
                            isEntityOpen,
                            selectedEntities,
                            searchEntityResults: combinedResults,
                        },
                    });
                });
        }, 500),
        [selectedEntities]
    );
    const entityOption = entityList?.find(
        (item) => item.id === name
    )?.valuesList;

    return (
        <>
            <>
                <GenericSingleSelect
                    label="Select Entity"
                    autocompleteProps={{
                        options: entityList ?? [],
                        disableClearable: false,
                    }}
                    value={name}
                    setValue={(e) => {
                        updateState({
                            entity: {
                                name: e,
                                value: [],
                                isEntityOpen: !entityList?.find(
                                    (item) => item.id === e
                                )?.valuesList?.length,
                                searchEntityResults: [],
                                selectedEntities: [],
                            },
                        });
                    }}
                    loading={entityListLoading}
                />
            </>
            {name && (
                <Box mt={2}>
                    {isEntityOpen ? (
                        <GenericMultipleSelect<EntityValueType>
                            label="Search for Open ended values"
                            values={value}
                            setValues={(newValues) => {
                                const existingSelectedEntities =
                                    searchEntityResults?.filter((item) =>
                                        newValues.includes(item.id)
                                    );
                                updateState({
                                    entity: {
                                        name,
                                        value: newValues,
                                        isEntityOpen,
                                        searchEntityResults:
                                            newValues.length > 0
                                                ? searchEntityResults
                                                : [],
                                        selectedEntities:
                                            newValues.length > 0
                                                ? existingSelectedEntities
                                                : [],
                                    },
                                });
                            }}
                            autocompleteProps={{
                                options: searchEntityResults ?? [],
                                loading: searchResultsFetching,
                                filterOptions: filterOptions,
                                onInputChange: (_, newInputValue) => {
                                    if (newInputValue.length > 0) {
                                        debouncedSearchEntity(
                                            newInputValue,
                                            name
                                        );
                                    }
                                },
                                disableClearable: false,
                            }}
                            placeholder="Search"
                        />
                    ) : (
                        <GenericMultipleSelect<EntityValueType>
                            label="Select Value"
                            values={value}
                            setValues={(e) => {
                                updateState({
                                    entity: {
                                        name,
                                        value: e,
                                        isEntityOpen,
                                    },
                                });
                            }}
                            autocompleteProps={{
                                options: entityOption ?? [],
                                disableClearable: false,
                            }}
                        />
                    )}
                </Box>
            )}
        </>
    );
};
